import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { designSystem, commonStyles } from '../../styles/design-system';
import { 
  ArrowLeftIcon, 
  SettingsIcon, 
  SaveIcon,
  UploadIcon,
  DownloadIcon,
  RefreshIcon
} from '../../utils/IconWrapper';

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: ${designSystem.colors.background.primary};
  padding: ${designSystem.spacing.xl};
  font-family: ${designSystem.typography.fontFamily.primary};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    padding: ${designSystem.spacing.md};
  }
`;

const Header = styled.div`
  ${commonStyles.card}
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${designSystem.spacing.md};
    text-align: center;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.md};

    h1 {
      ${commonStyles.gradientText}
      font-family: ${designSystem.typography.fontFamily.secondary};
      font-size: ${designSystem.typography.fontSize['3xl']};
      font-weight: ${designSystem.typography.fontWeight.bold};
      margin: 0;
    }
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${designSystem.spacing.xl};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SettingsCard = styled(motion.div)`
  ${commonStyles.card}

  h3 {
    ${commonStyles.titleSecondary}
    margin-bottom: ${designSystem.spacing.lg};
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.sm};

    .icon {
      font-size: ${designSystem.typography.fontSize.xl};
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${designSystem.spacing.lg};

  label {
    display: block;
    margin-bottom: ${designSystem.spacing.sm};
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    font-size: ${designSystem.typography.fontSize.sm};
  }

  input, textarea, select {
    ${commonStyles.input}
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .help-text {
    margin-top: ${designSystem.spacing.xs};
    font-size: ${designSystem.typography.fontSize.xs};
    color: ${designSystem.colors.text.secondary};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: ${designSystem.colors.text.white};
          border: 2px solid ${designSystem.colors.status.error};
          
          &:hover {
            background: transparent;
            color: ${designSystem.colors.status.error};
          }
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  font-size: ${designSystem.typography.fontSize.sm};
  margin-right: ${designSystem.spacing.sm};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: ${designSystem.colors.status.success}15;
  border: 1px solid ${designSystem.colors.status.success}30;
  border-radius: ${designSystem.borderRadius.lg};
  padding: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.lg};
  color: ${designSystem.colors.status.success};
  font-size: ${designSystem.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};

  &::before {
    content: '✅';
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.lg};
`;

const StatItem = styled.div`
  background: ${designSystem.colors.background.tertiary};
  padding: ${designSystem.spacing.md};
  border-radius: ${designSystem.borderRadius.lg};
  text-align: center;

  .value {
    font-size: ${designSystem.typography.fontSize.xl};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.primary.main};
    margin-bottom: ${designSystem.spacing.xs};
  }

  .label {
    font-size: ${designSystem.typography.fontSize.xs};
    color: ${designSystem.colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    totalBookings: 0,
    totalAccommodations: 0,
    databaseSize: '0 MB'
  });

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: 'noreply@j-o-h.com',
    fromName: 'Journey of History'
  });

  const [siteSettings, setSiteSettings] = useState({
    siteName: 'Journey of History',
    siteDescription: 'Scopri e prenota alloggi in luoghi storici unici',
    maintenanceMode: false,
    allowRegistrations: true,
    requireEmailVerification: true,
    requirePhoneVerification: false
  });

  const [paymentSettings, setPaymentSettings] = useState({
    stripePublicKey: '',
    stripeCurrency: 'EUR',
    serviceFeePercentage: '5',
    cleaningFeeDefault: '25'
  });

  useEffect(() => {
    loadSystemStats();
    loadSettings();
  }, []);

  const loadSystemStats = async () => {
    try {
      const [usersResult, bookingsResult, accommodationsResult] = await Promise.all([
        supabase.from('user_profiles').select('id', { count: 'exact', head: true }),
        supabase.from('bookings').select('id', { count: 'exact', head: true }),
        supabase.from('accommodations').select('id', { count: 'exact', head: true })
      ]);

      setSystemStats({
        totalUsers: usersResult.count || 0,
        totalBookings: bookingsResult.count || 0,
        totalAccommodations: accommodationsResult.count || 0,
        databaseSize: '12.5 MB' // Simulato
      });
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const loadSettings = async () => {
    // In un'app reale, questi settings sarebbero salvati nel database
    // Per ora usiamo valori di default
    console.log('Caricamento impostazioni...');
  };

  const saveEmailSettings = async () => {
    setLoading(true);
    try {
      // Simula salvataggio nel database
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Errore salvataggio:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSiteSettings = async () => {
    setLoading(true);
    try {
      // Simula salvataggio nel database
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Errore salvataggio:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePaymentSettings = async () => {
    setLoading(true);
    try {
      // Simula salvataggio nel database
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Errore salvataggio:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportDatabase = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const data = {
        exportDate: new Date().toISOString(),
        message: 'Database export completato'
      };
      
      const jsonData = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
    } catch (error) {
      console.error('Errore export:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsContainer>
      <Header>
        <div className="header-content">
          <Button variant="secondary" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeftIcon size={16} />
            Torna al Dashboard
          </Button>
          <h1>
            <SettingsIcon className="icon" size={32} />
            Impostazioni Sistema
          </h1>
        </div>
      </Header>

      {showSuccess && (
        <SuccessMessage
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          Impostazioni salvate con successo!
        </SuccessMessage>
      )}

      <SettingsGrid>
        {/* Statistiche Sistema */}
        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3>
            <span className="icon">📊</span>
            Statistiche Sistema
          </h3>
          
          <StatsSection>
            <StatItem>
              <div className="value">{systemStats.totalUsers}</div>
              <div className="label">Utenti</div>
            </StatItem>
            <StatItem>
              <div className="value">{systemStats.totalBookings}</div>
              <div className="label">Prenotazioni</div>
            </StatItem>
            <StatItem>
              <div className="value">{systemStats.totalAccommodations}</div>
              <div className="label">Alloggi</div>
            </StatItem>
            <StatItem>
              <div className="value">{systemStats.databaseSize}</div>
              <div className="label">Database</div>
            </StatItem>
          </StatsSection>

          <Button onClick={loadSystemStats} disabled={loading}>
            <RefreshIcon size={16} />
            Aggiorna Statistiche
          </Button>

          <Button variant="secondary" onClick={exportDatabase} disabled={loading}>
            <DownloadIcon size={16} />
            {loading ? 'Esportazione...' : 'Esporta Database'}
          </Button>
        </SettingsCard>

        {/* Impostazioni Email */}
        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3>
            <span className="icon">📧</span>
            Configurazione Email
          </h3>

          <FormGroup>
            <label>Host SMTP</label>
            <input
              type="text"
              value={emailSettings.smtpHost}
              onChange={(e) => setEmailSettings({...emailSettings, smtpHost: e.target.value})}
              placeholder="smtp.gmail.com"
            />
          </FormGroup>

          <FormGroup>
            <label>Porta SMTP</label>
            <input
              type="number"
              value={emailSettings.smtpPort}
              onChange={(e) => setEmailSettings({...emailSettings, smtpPort: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <label>Username SMTP</label>
            <input
              type="email"
              value={emailSettings.smtpUser}
              onChange={(e) => setEmailSettings({...emailSettings, smtpUser: e.target.value})}
              placeholder="admin@j-o-h.com"
            />
          </FormGroup>

          <FormGroup>
            <label>Password SMTP</label>
            <input
              type="password"
              value={emailSettings.smtpPassword}
              onChange={(e) => setEmailSettings({...emailSettings, smtpPassword: e.target.value})}
              placeholder="••••••••"
            />
          </FormGroup>

          <FormGroup>
            <label>Email Mittente</label>
            <input
              type="email"
              value={emailSettings.fromEmail}
              onChange={(e) => setEmailSettings({...emailSettings, fromEmail: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <label>Nome Mittente</label>
            <input
              type="text"
              value={emailSettings.fromName}
              onChange={(e) => setEmailSettings({...emailSettings, fromName: e.target.value})}
            />
          </FormGroup>

          <Button onClick={saveEmailSettings} disabled={loading}>
            <SaveIcon size={16} />
            {loading ? 'Salvataggio...' : 'Salva Email'}
          </Button>
        </SettingsCard>

        {/* Impostazioni Sito */}
        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3>
            <span className="icon">🌐</span>
            Configurazione Sito
          </h3>

          <FormGroup>
            <label>Nome Sito</label>
            <input
              type="text"
              value={siteSettings.siteName}
              onChange={(e) => setSiteSettings({...siteSettings, siteName: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <label>Descrizione Sito</label>
            <textarea
              value={siteSettings.siteDescription}
              onChange={(e) => setSiteSettings({...siteSettings, siteDescription: e.target.value})}
              rows={3}
            />
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={siteSettings.maintenanceMode}
                onChange={(e) => setSiteSettings({...siteSettings, maintenanceMode: e.target.checked})}
                style={{ marginRight: designSystem.spacing.sm }}
              />
              Modalità Manutenzione
            </label>
            <div className="help-text">Il sito sarà visibile solo agli amministratori</div>
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={siteSettings.allowRegistrations}
                onChange={(e) => setSiteSettings({...siteSettings, allowRegistrations: e.target.checked})}
                style={{ marginRight: designSystem.spacing.sm }}
              />
              Consenti Registrazioni
            </label>
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={siteSettings.requireEmailVerification}
                onChange={(e) => setSiteSettings({...siteSettings, requireEmailVerification: e.target.checked})}
                style={{ marginRight: designSystem.spacing.sm }}
              />
              Richiedi Verifica Email
            </label>
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={siteSettings.requirePhoneVerification}
                onChange={(e) => setSiteSettings({...siteSettings, requirePhoneVerification: e.target.checked})}
                style={{ marginRight: designSystem.spacing.sm }}
              />
              Richiedi Verifica Telefono
            </label>
          </FormGroup>

          <Button onClick={saveSiteSettings} disabled={loading}>
            <SaveIcon size={16} />
            {loading ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </SettingsCard>

        {/* Impostazioni Pagamenti */}
        <SettingsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3>
            <span className="icon">💳</span>
            Configurazione Pagamenti
          </h3>

          <FormGroup>
            <label>Chiave Pubblica Stripe</label>
            <input
              type="text"
              value={paymentSettings.stripePublicKey}
              onChange={(e) => setPaymentSettings({...paymentSettings, stripePublicKey: e.target.value})}
              placeholder="pk_test_..."
            />
            <div className="help-text">Chiave pubblica per i pagamenti Stripe</div>
          </FormGroup>

          <FormGroup>
            <label>Valuta</label>
            <select
              value={paymentSettings.stripeCurrency}
              onChange={(e) => setPaymentSettings({...paymentSettings, stripeCurrency: e.target.value})}
            >
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - Dollaro USA</option>
              <option value="GBP">GBP - Sterlina</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>Commissione di Servizio (%)</label>
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              value={paymentSettings.serviceFeePercentage}
              onChange={(e) => setPaymentSettings({...paymentSettings, serviceFeePercentage: e.target.value})}
            />
            <div className="help-text">Percentuale di commissione su ogni prenotazione</div>
          </FormGroup>

          <FormGroup>
            <label>Costo Pulizia Standard (€)</label>
            <input
              type="number"
              min="0"
              value={paymentSettings.cleaningFeeDefault}
              onChange={(e) => setPaymentSettings({...paymentSettings, cleaningFeeDefault: e.target.value})}
            />
            <div className="help-text">Costo di pulizia di default per gli alloggi</div>
          </FormGroup>

          <Button onClick={savePaymentSettings} disabled={loading}>
            <SaveIcon size={16} />
            {loading ? 'Salvataggio...' : 'Salva Pagamenti'}
          </Button>
        </SettingsCard>
      </SettingsGrid>
    </SettingsContainer>
  );
};

export default AdminSettings; 